import { baseApi } from './baseApi';
import { toFormData } from './apiUtils';

export const giveawayApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getGroupGiveaways: builder.query({
      query: (groupId) => `/host/groups/${groupId}/giveaways`,
      providesTags: (result, error, groupId) =>
        result
          ? [
              ...result.map((g) => ({ type: 'Giveaway', id: g.id })),
              { type: 'Giveaway', id: `GROUP-${groupId}` },
            ]
          : [{ type: 'Giveaway', id: `GROUP-${groupId}` }],
    }),
    getGiveaway: builder.query({
      query: (id) => `/host/giveaways/${id}`,
      providesTags: (result, error, id) => [{ type: 'Giveaway', id }],
    }),
    getAddressSuggestions: builder.query({
      query: (q) => ({ url: '/host/address-suggestions', params: { q } }),
    }),
    createGiveaway: builder.mutation({
      query: ({
        groupId,
        title,
        location,
        radiusMiles,
        food,
        servingsLeft,
        allergens,
        photoUrls,
        photoFiles,
      }) => {
        if (photoFiles?.length) {
          return {
            url: '/host/giveaways',
            method: 'POST',
            body: toFormData([
              ['group_id', groupId],
              ['title', title],
              ['location', location],
              ['radius_miles', radiusMiles],
              ['food', food],
              ['servings_left', servingsLeft],
              ...(allergens ?? []).map((a) => ['allergens', a]),
              ...photoFiles.map((f) => ['photos', f]),
              ...(photoUrls ?? []).map((u) => ['photo_urls', u]),
            ]),
          };
        }
        return {
          url: '/host/giveaways',
          method: 'POST',
          body: {
            group_id: groupId,
            title,
            location,
            radius_miles: radiusMiles,
            food,
            servings_left: servingsLeft,
            allergens,
            photo_urls: photoUrls,
          },
        };
      },
      invalidatesTags: (result, error, { groupId }) => [
        { type: 'Giveaway', id: `GROUP-${groupId}` },
      ],
    }),
    updateServings: builder.mutation({
      query: ({ id, servingsLeft }) => ({
        url: `/host/giveaways/${id}/servings`,
        method: 'PATCH',
        body: { servings_left: servingsLeft },
      }),
      invalidatesTags: (result, error, { id, groupId }) => [
        { type: 'Giveaway', id },
        { type: 'Giveaway', id: `GROUP-${groupId}` },
      ],
    }),
    sendAlert: builder.mutation({
      query: ({ id, message }) => ({
        url: `/host/giveaways/${id}/alert`,
        method: 'POST',
        body: message ? { message } : {},
      }),
      invalidatesTags: (result, error, { id, groupId }) => [
        { type: 'Giveaway', id },
        { type: 'Giveaway', id: `GROUP-${groupId}` },
      ],
    }),
    getSchedule: builder.query({
      query: () => '/host/schedule',
      providesTags: (result) =>
        result
          ? [...result.map((s) => ({ type: 'ScheduledAlert', id: s.id })), { type: 'ScheduledAlert', id: 'LIST' }]
          : [{ type: 'ScheduledAlert', id: 'LIST' }],
    }),
    scheduleAlert: builder.mutation({
      query: ({ id, scheduledAt, message }) => ({
        url: `/host/giveaways/${id}/schedule`,
        method: 'POST',
        body: { scheduled_at: scheduledAt, ...(message ? { message } : {}) },
      }),
      invalidatesTags: [{ type: 'ScheduledAlert', id: 'LIST' }],
    }),
    rescheduleAlert: builder.mutation({
      query: ({ id, scheduledAt }) => ({
        url: `/host/schedule/${id}`,
        method: 'PATCH',
        body: { scheduled_at: scheduledAt },
      }),
      invalidatesTags: [{ type: 'ScheduledAlert', id: 'LIST' }],
    }),
    deleteScheduledAlert: builder.mutation({
      query: (id) => ({ url: `/host/schedule/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'ScheduledAlert', id: 'LIST' }],
    }),
    endGiveaway: builder.mutation({
      query: ({ id }) => ({ url: `/host/giveaways/${id}/end`, method: 'POST' }),
      invalidatesTags: (result, error, { id, groupId }) => [
        { type: 'Giveaway', id },
        { type: 'Giveaway', id: `GROUP-${groupId}` },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetGroupGiveawaysQuery,
  useGetGiveawayQuery,
  useLazyGetAddressSuggestionsQuery,
  useCreateGiveawayMutation,
  useUpdateServingsMutation,
  useSendAlertMutation,
  useGetScheduleQuery,
  useScheduleAlertMutation,
  useRescheduleAlertMutation,
  useDeleteScheduledAlertMutation,
  useEndGiveawayMutation,
} = giveawayApi;
